<script setup lang="ts">
import { computed } from "vue";
import { fromMaybe, isJust, just, nothing } from "../../lib/maybe";
import { useProjectWorkspaceStore } from "../../stores/useProjectWorkspaceStore";
import {
  ARC_MOMENT_TRAY_KEY,
  listUnmappedArcMoments,
} from "../../lib/storyboardArcs";
import { useOutlineEntitiesStore } from "../../stores/useOutlineEntitiesStore";
import {
  useStoryboardDragStore,
  type BoardDragSource,
} from "../../stores/useStoryboardDragStore";
import { IconGripVertical } from "../ribbonIcons";

import { entityEffectiveColor } from "../../lib/entityColors";
import { displayFileName } from "../../lib/fileNames";
import { groupSceneLinkEntries } from "../../lib/sceneLinkOptions";

const entities = useOutlineEntitiesStore();
const drag = useStoryboardDragStore();
const workspace = useProjectWorkspaceStore();
function openEntry(name: string): void {
  if (drag.consumePointerGestureWasDrag()) return;
  void workspace.onSelectProjectDatabase(name);
}

const entityGroups = computed(() =>
  groupSceneLinkEntries(entities.databaseEntryFields),
);
function entityDragSource(entryFile: string): BoardDragSource {
  return {
    key: `entity:${entryFile}`,
    kind: "entity",
    folder: nothing,
    actFolder: nothing,
    sceneFile: nothing,
    name: nothing,
    entryFile: just(entryFile),
    momentId: nothing,
  };
}

const unmappedMoments = computed(() =>
  listUnmappedArcMoments(entities.databaseEntryFields),
);

const trayDragOver = computed(
  () => isJust(drag.dragOver) && drag.dragOver.value === ARC_MOMENT_TRAY_KEY,
);

function momentDragSource(
  entryFile: string,
  momentId: string,
): BoardDragSource {
  return {
    key: `arc-moment:${entryFile}:${momentId}`,
    kind: "arc-moment",
    folder: nothing,
    actFolder: nothing,
    sceneFile: nothing,
    name: nothing,
    entryFile: just(entryFile),
    momentId: just(momentId),
  };
}
</script>

<template>
  <section
    class="zw-storyboard-unmapped"
    :class="{ 'zw-storyboard-unmapped-drag-over': trayDragOver }"
    :data-board-key="ARC_MOMENT_TRAY_KEY"
    data-board-kind="arc-moment-tray"
    aria-label="Unlinked events drop zone"
  >
    <section aria-label="Story arc">
      <h3>Story arc</h3>
      <p
        v-if="unmappedMoments.length === 0"
        class="zw-storyboard-unmapped-empty"
      >
        Every arc event is linked to a scene. Drag an event chip here to unlink
        it.
      </p>
      <ul v-else class="zw-storyboard-unmapped-list">
        <li
          v-for="item in unmappedMoments"
          :key="item.id"
          class="zw-storyboard-unmapped-item"
          :class="{
            'zw-storyboard-unmapped-item-dragging':
              isJust(drag.dragFrom) &&
              drag.dragFrom.value.key ===
                momentDragSource(item.entryFile, item.momentId).key,
          }"
        >
          <span
            class="zw-storyboard-unmapped-grip"
            aria-hidden="true"
            title="Drag onto a chapter or scene card to link this event"
            @pointerdown.stop="
              drag.onCardPointerDown(
                momentDragSource(item.entryFile, item.momentId),
                $event,
              )
            "
          >
            <IconGripVertical class="size-4" />
          </span>
          <div class="zw-storyboard-unmapped-body">
            <span class="zw-storyboard-unmapped-entity">{{
              item.entityLabel
            }}</span>
            <span class="zw-storyboard-unmapped-moment">{{ item.moment }}</span>
            <span v-if="isJust(item.at)" class="zw-storyboard-unmapped-at">{{
              item.at.value
            }}</span>
          </div>
        </li>
      </ul>
    </section>
    <section class="zw-storyboard-worldbuilding" aria-label="Worldbuilding">
      <h3>Worldbuilding</h3>
      <p class="zw-storyboard-unmapped-empty">
        Drag an entry onto a scene to link it.
      </p>
      <div
        v-for="[type, entries] in entityGroups"
        :key="type"
        class="zw-storyboard-link-group"
      >
        <h4>{{ type }}</h4>
        <ul class="zw-storyboard-unmapped-list">
          <li v-for="entry in entries" :key="entry.name">
            <button
              type="button"
              class="zw-storyboard-unmapped-item zw-storyboard-entity-row"
              :class="{
                'zw-storyboard-unmapped-item-dragging':
                  isJust(drag.dragFrom) &&
                  drag.dragFrom.value.key === entityDragSource(entry.name).key,
              }"
              @pointerdown.stop="
                drag.consumePointerGestureWasDrag();
                drag.onCardPointerDown(entityDragSource(entry.name), $event);
              "
              @click="openEntry(entry.name)"
            >
              <IconGripVertical
                class="zw-storyboard-unmapped-grip size-4"
                aria-hidden="true"
              />
              <span
                class="zw-storyboard-entity-dot"
                aria-hidden="true"
                :style="{
                  background:
                    fromMaybe(
                      entityEffectiveColor(
                        entry.fields.color,
                        entry.fields.type,
                      ),
                    ) ?? 'var(--text-muted)',
                }"
              />
              <span class="zw-storyboard-entity-name">{{
                displayFileName(entry.name)
              }}</span>
            </button>
          </li>
        </ul>
      </div>
    </section>
  </section>
</template>

<style scoped>
.zw-storyboard-unmapped {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  flex: 1;
  padding: var(--space-xs);
  border: 1px dashed transparent;
  border-radius: 0.3rem;
}
.zw-storyboard-unmapped-drag-over {
  border-color: var(--color-zw-amber);
  background: color-mix(in oklch, var(--color-zw-amber) 8%, transparent);
}
.zw-storyboard-unmapped-empty {
  margin: 0;
  color: var(--text-muted);
  font-size: 0.8rem;
  line-height: 1.45;
}
.zw-storyboard-link-group {
  margin-top: var(--space-xl);
}
.zw-storyboard-worldbuilding {
  margin-top: var(--space-xl);
  padding-top: var(--space-lg);
  border-top: 1px solid var(--line);
}
h3,
h4 {
  margin: 0 0 var(--space-sm);
  font-size: 0.7rem;
  line-height: 1.3;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
}
h3 {
  color: var(--text);
  font-size: 0.75rem;
}
.zw-storyboard-unmapped-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
}
.zw-storyboard-unmapped-item {
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  padding: var(--space-sm);
  border: 0;
  border-radius: 0.3rem;
  background: transparent;
  color: var(--text);
  font: inherit;
  font-size: 0.85rem;
  line-height: 1.35;
  text-align: left;
  min-width: 0;
}
.zw-storyboard-unmapped-item:hover,
.zw-storyboard-unmapped-item:focus-within {
  background: var(--bg-raised);
}
.zw-storyboard-entity-row {
  width: 100%;
  align-items: center;
  cursor: grab;
}
.zw-storyboard-entity-row:active {
  cursor: grabbing;
}
.zw-storyboard-entity-row:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 1px;
}
.zw-storyboard-unmapped-item-dragging {
  opacity: 0.45;
}
.zw-storyboard-unmapped-grip {
  flex-shrink: 0;
  width: 1rem;
  height: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  cursor: grab;
  opacity: 0.4;
}
.zw-storyboard-unmapped-item:hover .zw-storyboard-unmapped-grip,
.zw-storyboard-unmapped-item:focus-within .zw-storyboard-unmapped-grip {
  opacity: 1;
}
.zw-storyboard-entity-dot {
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 50%;
  flex-shrink: 0;
}
.zw-storyboard-entity-name {
  min-width: 0;
  overflow-wrap: anywhere;
}
.zw-storyboard-unmapped-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  min-width: 0;
  overflow-wrap: anywhere;
}
.zw-storyboard-unmapped-entity,
.zw-storyboard-unmapped-at {
  font-size: 0.72rem;
  color: var(--text-muted);
}
.zw-storyboard-unmapped-entity {
  font-weight: 600;
}
.zw-storyboard-unmapped-moment {
  font-size: 0.85rem;
  line-height: 1.35;
}
@media (pointer: coarse) {
  .zw-storyboard-entity-row {
    min-height: 44px;
  }
}
</style>
